import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import api from "../../utils/api";
import toast from "react-hot-toast";
import "./SideCart.css";
import { optimizedUrl } from "./Image";
import { packSize } from "../../utils/format";

export default function SideCart() {
  const { cart, cartCount, cartTotal, updateQuantity, removeFromCart, isSideCartOpen, toggleCart } = useCart();
  const [couponCode, setCouponCode] = useState(localStorage.getItem("akb_applied_coupon") || "");
  const [isApplying, setIsApplying] = useState(false);
  const navigate = useNavigate();

  const [discountAmount, setDiscountAmount] = useState(0);

  // Auto-check on open if coupon in storage
  useEffect(() => {
    if (isSideCartOpen && couponCode && cartTotal > 0 && discountAmount === 0) {
      validateCoupon(couponCode);
    }
  }, [isSideCartOpen, cartTotal]);

  const validateCoupon = async (code) => {
    try {
      const res = await api.post("/offers/validate", { code, cartTotal });
      if (res.data.success) {
        setDiscountAmount(res.data.discount);
        return true;
      }
    } catch (err) {
      // Silently fail for auto-check
      return false;
    }
  };

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;
    setIsApplying(true);
    
    try {
      const res = await api.post("/offers/validate", { code: couponCode.trim(), cartTotal });
      if (res.data.success) {
        setDiscountAmount(res.data.discount);
        localStorage.setItem("akb_applied_coupon", couponCode.trim());
        toast.success(`Coupon applied! ₹${res.data.discount} saved.`);
      }
    } catch (err) {
      setDiscountAmount(0);
      localStorage.removeItem("akb_applied_coupon");
      toast.error(err.response?.data?.message || "Invalid coupon code");
    } finally {
      setIsApplying(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setDiscountAmount(0);
    localStorage.removeItem("akb_applied_coupon");
    toast.success("Coupon removed");
  };

  const handleCheckout = () => {
    toggleCart(false);
    navigate("/checkout", { state: { couponCode, discount: discountAmount, deliveryCharge: 0 } });
  };

  return (
    <div className={`sidecart-overlay ${isSideCartOpen ? "open" : ""}`} onClick={() => toggleCart(false)}>
      <div className={`sidecart-drawer ${isSideCartOpen ? "open" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="sidecart-header">
          <h2>Your Cart ({cartCount})</h2>
          <button className="sidecart-close" onClick={() => toggleCart(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div className="sidecart-body">
          {cartCount === 0 ? (
            <div className="sidecart-empty">
              <p>Your cart is empty.</p>
              <button className="btn-shop-now" onClick={() => toggleCart(false)}>Continue Shopping</button>
            </div>
          ) : (
            <div className="sidecart-items">
              {cart.items.map((item) => {
                const p = item.productId || {};
                return (
                  <div key={p._id || Math.random()} className="sidecart-item">
                    <img src={optimizedUrl(p.images?.[0], 160) || "/akb1.png"} alt={p.name} className="sci-img" />
                    <div className="sci-details">
                      <div className="sci-title">{p.name}</div>
                      {packSize(p.weight) && <div className="sci-weight">{packSize(p.weight)}</div>}
                      <div className="sci-price">
                        <span className="current-price">₹{p.sellPrice}</span>
                        {p.mrp > p.sellPrice && <span className="old-price">₹{p.mrp}</span>}
                      </div>
                      
                      <div className="sci-actions">
                        <div className="sci-qty-controls">
                          <button onClick={() => updateQuantity(p._id, item.quantity - 1)}>−</button>
                          <span>{item.quantity}</span>
                          <button onClick={() => updateQuantity(p._id, item.quantity + 1)}>+</button>
                        </div>
                        <button className="sci-remove" onClick={() => removeFromCart(p._id)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {cartCount > 0 && (
          <div className="sidecart-footer">
            <div className="sc-coupon-box">
              {discountAmount > 0 ? (
                <div className="applied-coupon-success">
                  <span className="sc-applied-code"> {couponCode} APPLIED</span>
                  <button className="sc-remove-coupon" onClick={handleRemoveCoupon}>Remove</button>
                </div>
              ) : (
                <>
                  <input 
                    type="text" 
                    placeholder="Enter Coupon Code" 
                    value={couponCode} 
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  />
                  <button onClick={handleApplyCoupon} disabled={isApplying || !couponCode}>
                    {isApplying ? "..." : "Apply"}
                  </button>
                </>
              )}
            </div>

            <div className="sc-summary">
              <div className="sc-row">
                <span>Subtotal</span>
                <span>₹{cartTotal}</span>
              </div>
              {discountAmount > 0 && (
                <div className="sc-row discount">
                  <span>Coupon Discount</span>
                  <span className="free">-₹{discountAmount}</span>
                </div>
              )}
              <div className="sc-row delivery">
                <span>Delivery Fee</span>
                <span className="free">FREE</span>
              </div>
              <div className="sc-row total">
                <span>Total Amount</span>
                <span>₹{Math.max(0, cartTotal - discountAmount)}</span>
              </div>
            </div>

            <div className="checkout-action-wrapper" style={{ position: 'relative', marginTop: '24px' }}>
              {discountAmount <= 150 && (
                <div style={{
                  position: 'absolute',
                  top: '-16px',
                  left: '12px',
                  backgroundColor: '#4ade80',
                  color: '#000',
                  fontSize: '16px',
                  fontWeight: '800',
                  padding: '6px 16px',
                  borderRadius: '6px',
                  zIndex: 2,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  Upto 10% off on prepaid
                </div>
              )}
              <button className="btn-sc-checkout" onClick={handleCheckout} style={{ position: 'relative', overflow: 'hidden' }}>
                PROCEED TO CHECKOUT <span>&rarr;</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
